import React from "react";
import { useFocusEffect } from "@react-navigation/native";

type tQueryState<T> = {
  isLoading: boolean;
  isError: boolean;
  data?: T;
  error: unknown;
};

type tMutationState = {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
};

type tQueryResult<T> = tQueryState<T> & { setQueryData: (data: T) => void; reset: () => void; refetch: () => void };

type tMutationResult<T> = tMutationState & { mutateAsync: () => Promise<T> };

type tQueryOptions = {
  enabled?: boolean;
  refetchOnFocus?: boolean;
};

const initialState = {
  isLoading: false,
  isError: false,
  data: undefined,
  error: undefined,
};

export const useQuery = <T extends unknown>(
  queryFn: () => Promise<T>,
  queryOptions: tQueryOptions = { enabled: true },
): tQueryResult<T> => {
  const { enabled } = queryOptions;
  const [state, setState] = React.useState<tQueryState<T>>(initialState);
  const isFetchingRef = React.useRef(false);

  const setQueryData = React.useCallback((data: T) => {
    setState((prev) => ({ ...prev, data }));
  }, []);

  const reset = React.useCallback(() => {
    setState(initialState);
  }, []);

  const refetch = React.useCallback(async () => {
    if (isFetchingRef.current) return;

    try {
      setState((prev) => ({ ...prev, isLoading: true }));
      isFetchingRef.current = true;
      const data = await queryFn();
      setState((prev) => ({ ...prev, isLoading: false, data }));
    } catch (e) {
      setState((prev) => ({ ...prev, isLoading: false, isError: true, error: e }));
    } finally {
      isFetchingRef.current = false;
    }
  }, [queryFn]);

  React.useEffect(() => {
    if (enabled) {
      refetch();
    }
  }, [enabled, refetch]);

  const { isLoading, isError, data, error } = state;
  return React.useMemo(
    () => ({ isLoading, isError, data, error, setQueryData, reset, refetch }),
    [data, error, isError, isLoading, refetch, reset, setQueryData],
  );
};

export const useFocusableQuery = <T extends unknown>(
  queryFn: () => Promise<T>,
  queryOptions: tQueryOptions = { enabled: true },
): tQueryResult<T> => {
  const query = useQuery(queryFn, queryOptions);
  const { refetch } = query;

  useFocusEffect(
    React.useCallback(() => {
      refetch();
    }, [refetch]),
  );

  return query;
};

export const useMutation = <T extends unknown>(queryFn: () => Promise<T>): tMutationResult<T> => {
  const [state, setState] = React.useState<tMutationState>({
    isLoading: false,
    isError: false,
    error: undefined,
  });

  const mutateAsync = React.useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true }));
      const result = await queryFn();
      setState((prev) => ({ ...prev, isLoading: false }));
      return result;
    } catch (e) {
      setState((prev) => ({ ...prev, isLoading: false, isError: true, error: e }));
      throw e;
    }
  }, [queryFn]);

  const { isLoading, isError, error } = state;
  return React.useMemo(() => ({ isLoading, isError, error, mutateAsync }), [error, isError, isLoading, mutateAsync]);
};
